import { useState, useRef, useEffect, useContext } from "react"
import {
  FaChevronDown,
  FaFilter,
  FaTrain,
  FaThermometerHalf,
  FaTachometerAlt,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaSubway,
  FaExclamationCircle,
  FaFilePdf,
  FaFileExcel,
  FaExclamationTriangle,
  FaSearch,
  FaTimes,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaArrowUp,
  FaArrowDown,
  FaExchangeAlt,
} from "react-icons/fa"
import { PiRecordFill, PiSirenFill } from "react-icons/pi"
import { IoIosWarning } from "react-icons/io"
import { LuChartNoAxesCombined } from "react-icons/lu"
import api from "../../services/AxiosConnection"
import AuthContext from "../../context/AuthContext"
import { useNotification } from "../../context/NotificationContext"
import NotificationSnackbar from "../../utils/NotificationSnackbar"
import DownloadSiteData from "../../utils/DownloadSiteData"
import DownloadSiteDataExcel from "../../utils/DownloadSiteDataExcel"
import { Vortex } from "react-loader-spinner"

const SiteOverview = () => {
  const [activeTab, setActiveTab] = useState("Train Reports")
  const [selectedSites, setSelectedSites] = useState([])
  const [showSiteDropdown, setShowSiteDropdown] = useState(false)
  const [fromDate, setFromDate] = useState("")
  const [toDate, setToDate] = useState("")
  const [sites, setSites] = useState([])
  const [trainData, setTrainData] = useState([])
  const [last24HoursCounts, setLast24HoursCounts] = useState({ normal: 0, warnings: 0, total: 0 })
  const [loading, setLoading] = useState(false)
  const [initialLoad, setInitialLoad] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const entriesPerPage = 10
  const dropdownRef = useRef(null)
  const sortDropdownRef = useRef(null)
  const { user } = useContext(AuthContext)
  const { notification, triggerNotification, closeNotification } = useNotification()
  const [showPdfTooltip, setShowPdfTooltip] = useState(false)
  const [showExcelTooltip, setShowExcelTooltip] = useState(false)
  const pdfHoverTimeout = useRef(null)
  const excelHoverTimeout = useRef(null)
  
  // Sorting state - set default sort option to date and direction to desc
  const [showSortPopup, setShowSortPopup] = useState(false)
  const [sortOption, setSortOption] = useState("date")
  const [sortDirection, setSortDirection] = useState("desc")
  
  // Set default dates to last 7 days
  useEffect(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const last7Days = new Date(today)
    last7Days.setDate(last7Days.getDate() - 6)
    const formatLocalDate = (date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, "0")
      const day = String(date.getDate()).padStart(2, "0")
      return `${year}-${month}-${day}`
    }
    setFromDate(formatLocalDate(last7Days))
    setToDate(formatLocalDate(today))
  }, [])
  
  // Fetch sites assigned to the user
  useEffect(() => {
    if (user && user.id) {
      api
        .get(`/users/get-sites`, { params: { userId: user.id } })
        .then((response) => {
          if (response.data && response.data.sites) {
            const fetchedSites = response.data.sites
            setSites(fetchedSites)
            if (fetchedSites.length > 0 && selectedSites.length === 0) {
              setSelectedSites([fetchedSites[0].siteName])
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching sites:", error)
          triggerNotification("Error fetching sites", "error")
        })
    }
  }, [user?.id, triggerNotification])
  
  // Fetch train count for last 24 hours
  useEffect(() => {
    if (selectedSites.length > 0 && sites.length > 0) {
      const fetchLast24HoursCounts = async () => {
        try {
          const siteDbs = selectedSites
            .map((siteName) => sites.find((s) => s.siteName === siteName)?.siteDbName)
            .filter(Boolean)
            .join(",")
          if (!siteDbs) {
            setLast24HoursCounts({ normal: 0, warnings: 0, total: 0 })
            return
          }
          const response = await api.post("/site-overview/getTrainCountLast24Hours", { siteDbs })
          const trainCountData = response.data.trainCountLast24Hours || {}
          let totalNormal = 0
          let totalWarnings = 0
          let totalTrains = 0
          Object.values(trainCountData).forEach((siteData) => {
            totalTrains += siteData.TotalTrains || 0
            totalWarnings += siteData.TrainsWithAlert || 0
            totalNormal += siteData.NormalTrains || 0
          })
          setLast24HoursCounts({
            normal: totalNormal,
            warnings: totalWarnings,
            total: totalTrains,
          })
        } catch (error) {
          console.error("Error fetching last 24 hours train counts:", error)
          triggerNotification("Error fetching last 24 hours train counts", "error")
          setLast24HoursCounts({ normal: 0, warnings: 0, total: 0 })
        }
      }
      fetchLast24HoursCounts()
    } else {
      setLast24HoursCounts({ normal: 0, warnings: 0, total: 0 })
    }
  }, [selectedSites, sites, triggerNotification])
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSiteDropdown(false)
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setShowSortPopup(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])
  
  // Fetch data on initial load
  useEffect(() => {
    if (initialLoad && sites.length > 0 && selectedSites.length > 0 && fromDate && toDate) {
      handleSearch()
      setInitialLoad(false)
    }
  }, [sites, selectedSites, fromDate, toDate, initialLoad])
  
  // Reset currentPage to 1 when activeTab, selectedSites, or sorting changes
  useEffect(() => {
    setCurrentPage(1)
  }, [activeTab, selectedSites, sortOption, sortDirection])
  
  // Date validation function
  const validateDates = () => {
    if (!fromDate || !toDate) {
      triggerNotification("Please select both from and to dates.", "error")
      return false
    }
    const fromDateObj = new Date(fromDate)
    const toDateObj = new Date(toDate)
    fromDateObj.setHours(0, 0, 0, 0)
    toDateObj.setHours(0, 0, 0, 0)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    if (fromDateObj > toDateObj) {
      triggerNotification("From date should be earlier than to date.", "error")
      return false
    }
    if (fromDateObj > today || toDateObj > today) {
      triggerNotification("Dates should not be greater than today's date.", "error")
      return false
    }
    const fifteenDaysInMs = 15 * 24 * 60 * 60 * 1000
    if (toDateObj - fromDateObj > fifteenDaysInMs) {
      triggerNotification("Date range cannot exceed 15 days.", "error")
      return false
    }
    return true
  }
  
  // Create date object from TrainId for sorting
  const createDateFromTrainId = (trainId) => {
    const parts = trainId.split("_")
    if (parts.length >= 6) {
      const [year, month, day, hour, min, sec] = parts
      return new Date(year, month - 1, day, hour, min, sec)
    }
    return new Date(0) // Return epoch if parsing fails
  }
  
  // Parse dateTime from TrainId
  const parseDateTimeFromTrainId = (trainId) => {
    const parts = trainId.split("_")
    if (parts.length >= 6) {
      const [year, month, day, hour, min, sec] = parts
      const date = new Date(year, month - 1, day, hour, min, sec)
      const formattedDate = date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      const formattedTime = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }).replace(":00", "")
      return `${formattedDate} ${formattedTime}`
    }
    return "N/A"
  }
  
  // Format number to 2 decimal places
  const formatNumber = (value) => {
    if (typeof value === "number") {
      return value.toFixed(2)
    }
    return "N/A"
  }
  
  // Handle search button click
  const handleSearch = async () => {
    if (!selectedSites.length) {
      triggerNotification("Please select at least one site.", "error")
      return
    }
    if (!validateDates()) {
      return
    }
    setLoading(true)
    try {
      const siteDbs = selectedSites
        .map((siteName) => sites.find((s) => s.siteName === siteName)?.siteDbName)
        .filter(Boolean)
        .join(",")
      if (!siteDbs) {
        triggerNotification("No valid site databases found.", "error")
        return
      }
      const response = await api.post("/site-overview/getTrainIdsByConditions", {
        siteDbs,
        fromDate,
        toDate,
      })
      const trainDataBySiteDb = response.data.trainDataBySiteDb || {}
      const siteDbToName = sites.reduce((acc, s) => {
        acc[s.siteDbName] = s.siteName
        return acc
      }, {})
      const flattenedData = []
      Object.keys(trainDataBySiteDb).forEach((siteDb) => {
        const trains = trainDataBySiteDb[siteDb]
        const siteName = siteDbToName[siteDb] || siteDb
        trains.forEach((train, index) => {
          const status =
            train.AlarmType === "hasAlarm"
              ? "critical"
              : train.WarningType === "hasWarning"
                ? "warning"
                : "normal"
          flattenedData.push({
            id: `${siteDb}-${index}`,
            siteName,
            dateTime: parseDateTimeFromTrainId(train.TrainId),
            direction: train.Direction || "N/A",
            trainType: train.Description || "N/A",
            maxAxleTemp: formatNumber(train.MaxAxleTemp),
            avgAxleTemp: formatNumber(train.AvgAxleTemp),
            ambientTemp: formatNumber(train.AtmosphereTemp),
            maxWheelTemp: formatNumber(train.MaxWheelTemp),
            avgWheelTemp: formatNumber(train.AvgWheelTemp),
            axles: train.TotalNoOfAxles || "N/A",
            speed: formatNumber(train.AvgSpeed),
            status,
            // Add raw date for sorting
            rawDate: createDateFromTrainId(train.TrainId),
          })
        })
      })
      setTrainData(flattenedData)
      setCurrentPage(1) // Reset to first page on new search
      if (flattenedData.length > 0) {
        triggerNotification(`Train data fetched successfully for ${fromDate} to ${toDate}`, "success")
      } else {
        triggerNotification("No train data found for the selected criteria", "info")
      }
    } catch (error) {
      console.error("Error fetching train data:", error)
      triggerNotification("Error fetching train data", "error")
      setTrainData([])
      setCurrentPage(1) // Reset to first page on error
    } finally {
      setLoading(false)
    }
  }
  
  const handleSiteToggle = (site) => {
    setSelectedSites((prev) => (prev.includes(site) ? prev.filter((s) => s !== site) : [...prev, site]))
  }
  
  const handleRemoveSite = (site) => {
    setSelectedSites((prev) => prev.filter((s) => s !== site))
  }
  
  const handleExportToPDF = () => {
    DownloadSiteData(selectedSites, sites, trainData, fromDate, toDate, setLoading, triggerNotification)
  }
  
  const handleExportToExcel = () => {
    DownloadSiteDataExcel(selectedSites, trainData, fromDate, toDate, setLoading, triggerNotification)
  }
  
  // Handle sort option click
  const handleSortClick = (option) => {
    if (sortOption === option) {
      // Toggle direction if same option is clicked
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortOption(option)
      setSortDirection("asc")
    }
    setShowSortPopup(false)
  }
  
  // Get sort option display text
  const getSortOptionText = () => {
    switch (sortOption) {
      case "site":
        return "Site"
      case "date":
        return "Date"
      case "trainType":
        return "Train Type"
      case "direction":
        return "Direction"
      default:
        return ""
    }
  }
  
  // Get sort icon based on current sort option and direction
  const getSortIcon = () => {
    if (sortDirection === "asc") {
      return <FaArrowUp className="w-4 h-4 text-blue-600" />
    } else {
      return <FaArrowDown className="w-4 h-4 text-blue-600" />
    }
  }
  
  // Sort train data based on current sort option and direction
  const getSortedData = (data) => {
    if (!sortOption) return data;
    
    const sortedData = [...data].sort((a, b) => {
      let valueA, valueB;
      
      switch (sortOption) {
        case "site":
          valueA = a.siteName || "";
          valueB = b.siteName || "";
          break;
        case "date":
          // Use raw date for proper sorting
          valueA = a.rawDate || new Date(0);
          valueB = b.rawDate || new Date(0);
          break;
        case "trainType":
          valueA = a.trainType || "";
          valueB = b.trainType || "";
          break;
        case "direction":
          valueA = a.direction || "";
          valueB = b.direction || "";
          break;
        default:
          return 0;
      }
      
      if (sortDirection === "asc") {
        return valueA > valueB ? 1 : -1;
      } else {
        return valueA < valueB ? 1 : -1;
      }
    });
    
    return sortedData;
  }
  
  // Pagination logic
  const filteredData = activeTab === "Train Reports" 
    ? getSortedData(trainData) 
    : getSortedData(trainData.filter((train) => train.status !== "normal"))
  
  const totalEntries = filteredData.filter((train) => selectedSites.includes(train.siteName)).length
  const totalPages = Math.ceil(totalEntries / entriesPerPage)
  const startIndex = (currentPage - 1) * entriesPerPage
  const endIndex = Math.min(startIndex + entriesPerPage, totalEntries)
  const paginatedData = filteredData
    .filter((train) => selectedSites.includes(train.siteName))
    .slice(startIndex, endIndex)
  
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1)
    }
  }
  
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1)
    }
  }
  
  const getStatusIcon = (status) => {
    switch (status) {
      case "normal":
        return <PiRecordFill className="w-4 h-4 text-green-500" />
      case "warning":
        return <IoIosWarning className="w-4 h-4 text-yellow-500" />
      case "critical":
        return <PiSirenFill className="w-4 h-4 text-red-500" />
      default:
        return <PiRecordFill className="w-4 h-4 text-green-500" />
    }
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <Vortex
            visible={true}
            height={80}
            width={80}
            ariaLabel="vortex-loading"
            wrapperStyle={{}}
            wrapperClass="vortex-wrapper"
            colors={["#2563eb", "#1e40af", "#60a5fa", "#3b82f6", "#1d4ed8", "#93c5fd"]}
          />
        </div>
      )}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        type={notification.type}
        onClose={closeNotification}
      />
      <div className="p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 space-y-3 sm:space-y-0">
          <div className="flex flex-col space-y-1 w-full sm:w-auto sm:max-w-[60%]">
            <div className="flex items-center space-x-2">
              <FaMapMarkerAlt className="w-4 h-4 text-blue-600" />
              <h2 className="text-base font-bold text-gray-800 font-sans">List of Trains for Sites:</h2>
            </div>
            <div className="flex flex-wrap gap-1">
              {selectedSites.map((site) => (
                <div
                  key={site}
                  className="flex items-center space-x-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium"
                >
                  <span>{site}</span>
                  <button
                    onClick={() => handleRemoveSite(site)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <FaTimes className="w-2 h-2" />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div ref={dropdownRef} className="relative w-full sm:w-auto flex items-center space-x-1">
            <FaFilter className="w-3 h-3 text-blue-600" />
            <div className="relative w-full sm:w-auto">
              <button
                onClick={() => setShowSiteDropdown(!showSiteDropdown)}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors w-full sm:w-auto justify-between sm:justify-start"
              >
                <span className="text-xs font-medium">
                  {selectedSites.length > 0 ? `${selectedSites.length} Site${selectedSites.length > 1 ? 's' : ''} Selected` : "Filter Sites"}
                </span>
                <FaChevronDown className="w-3 h-3" />
              </button>
              {showSiteDropdown && (
                <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-[9] max-h-60 overflow-y-auto">
                  <div className="p-2">
                    <div className="text-xs font-medium text-gray-700 mb-1">Available Sites</div>
                    {sites.map((site, index) => (
                      <label
                        key={index}
                        className="flex items-center space-x-1 py-0.5 cursor-pointer hover:bg-gray-50 rounded px-1"
                      >
                        <input
                          type="checkbox"
                          checked={selectedSites.includes(site.siteName)}
                          onChange={() => handleSiteToggle(site.siteName)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-xs text-gray-700">{site.siteName}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-3 mb-4 border border-gray-200">
          <div className="flex flex-col space-y-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-3 sm:space-y-0 sm:space-x-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
                <div className="flex items-center space-x-1 w-full sm:w-auto">
                  <FaCalendarAlt className="w-3 h-3 text-blue-500 flex-shrink-0" />
                  <span className="text-xs font-medium text-gray-600 min-w-[36px]">From:</span>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="text-xs text-gray-800 border border-gray-300 rounded px-1 py-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
                  />
                </div>
                <div className="flex items-center space-x-1 w-full sm:w-auto">
                  <span className="text-xs font-medium text-gray-600 min-w-[36px]">To:</span>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="text-xs text-gray-800 border border-gray-300 rounded px-1 py-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
                  />
                </div>
                <button
                  onClick={handleSearch}
                  className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors w-full sm:w-auto"
                >
                  <FaSearch className="w-3 h-3" />
                  <span className="text-xs font-medium">Search</span>
                </button>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-6 w-full sm:w-auto">
                <div className="text-center w-full sm:w-auto">
                  <p className="text-xs text-gray-600 mb-1">Train Count For Selected Period</p>
                  <div className="flex items-center justify-center space-x-3">
                    <div className="flex items-center space-x-1">
                      <PiRecordFill className="w-3 h-3 text-green-500" />
                      <span className="text-xs font-medium text-gray-800">
                        {trainData.filter((train) => train.status === "normal").length}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <IoIosWarning className="w-3 h-3 text-yellow-500" />
                      <span className="text-xs font-medium text-gray-800">
                        {trainData.filter((train) => train.status === "warning" || train.status === "critical").length}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <LuChartNoAxesCombined className="w-3 h-3 text-blue-600" />
                      <span className="text-xs font-medium text-gray-800">{trainData.length}</span>
                    </div>
                  </div>
                </div>
                <div className="text-center w-full sm:w-auto">
                  <p className="text-xs text-gray-600 mb-1">Train Count For Last 24 Hours</p>
                  <div className="flex items-center justify-center space-x-3">
                    <div className="flex items-center space-x-1">
                      <PiRecordFill className="w-3 h-3 text-green-500" />
                      <span className="text-xs font-medium text-gray-800">{last24HoursCounts.normal}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <IoIosWarning className="w-3 h-3 text-yellow-500" />
                      <span className="text-xs font-medium text-gray-800">{last24HoursCounts.warnings}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <LuChartNoAxesCombined className="w-3 h-3 text-blue-600" />
                      <span className="text-xs font-medium text-gray-800">{last24HoursCounts.total}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-center space-x-3 mt-1">
              <div className="flex items-center space-x-1">
                <PiRecordFill className="w-3 h-3 text-green-500" />
                <span className="text-xs text-gray-600">Normal</span>
              </div>
              <div className="flex items-center space-x-1">
                <IoIosWarning className="w-3 h-3 text-yellow-500" />
                <span className="text-xs text-gray-600">Warnings</span>
              </div>
              <div className="flex items-center space-x-1">
                <LuChartNoAxesCombined className="w-3 h-3 text-blue-600" />
                <span className="text-xs text-gray-600">Total</span>
              </div>
            </div>
          </div>
        </div>
        <div className="mb-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-1 sm:space-y-0">
            <div className="flex flex-col sm:flex-row space-y-1 sm:space-y-0 sm:space-x-1">
              <button
                onClick={() => setActiveTab("Train Reports")}
                className={`flex items-center justify-center space-x-1 px-3 py-1 text-xs font-medium rounded-t-lg transition-colors w-full sm:w-auto ${activeTab === "Train Reports"
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                  }`}
              >
                <FaTrain className="w-3 h-3" />
                <span>Train Reports</span>
              </button>
              <button
                onClick={() => setActiveTab("Alerts")}
                className={`flex items-center justify-center space-x-1 px-3 py-1 text-xs font-medium rounded-t-lg transition-colors w-full sm:w-auto ${activeTab === "Alerts"
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                  }`}
              >
                <FaExclamationTriangle className="w-3 h-3" />
                <span>Alerts</span>
              </button>
            </div>
            <div className="flex space-x-1 relative">
              {/* Export to PDF */}
              <div
                className="relative group"
                onMouseEnter={() => {
                  pdfHoverTimeout.current = setTimeout(() => setShowPdfTooltip(true), 2000);
                }}
                onMouseLeave={() => {
                  clearTimeout(pdfHoverTimeout.current);
                  setShowPdfTooltip(false);
                }}
              >
                <button
                  onClick={handleExportToPDF}
                  className="p-1 bg-transparent text-red-600 rounded-lg hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                >
                  <FaFilePdf className="w-5 h-5" />
                </button>
                {showPdfTooltip && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs bg-black text-white rounded shadow-md whitespace-nowrap z-50 max-w-[calc(100vw-1rem)] overflow-hidden text-ellipsis">
                    Export to PDF
                  </div>
                )}
              </div>
              {/* Export to Excel */}
              <div
                className="relative group"
                onMouseEnter={() => {
                  excelHoverTimeout.current = setTimeout(() => setShowExcelTooltip(true), 2000);
                }}
                onMouseLeave={() => {
                  clearTimeout(excelHoverTimeout.current);
                  setShowExcelTooltip(false);
                }}
              >
                <button
                  onClick={handleExportToExcel}
                  className="p-1 bg-transparent text-green-600 rounded-lg hover:text-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
                >
                  <FaFileExcel className="w-5 h-5" />
                </button>
                {showExcelTooltip && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs bg-black text-white rounded shadow-md whitespace-nowrap z-50 max-w-[calc(100vw-1rem)] overflow-hidden text-ellipsis">
                    Export to Excel
                  </div>
                )}
              </div>
              {/* Sort Button */}
              <div ref={sortDropdownRef} className="relative">
                <button
                  onClick={() => setShowSortPopup(!showSortPopup)}
                  className="p-1 bg-transparent text-blue-600 rounded-lg hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors flex items-center space-x-1"
                >
                  <FaExchangeAlt className="w-5 h-5" />
                  <span className="text-xs font-medium text-blue-600">
                    {getSortOptionText()}
                  </span>
                  {getSortIcon()}
                </button>
                {showSortPopup && (
                  <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <div className="py-1">
                      <div
                        className={`px-3 py-2 cursor-pointer flex justify-between items-center text-sm ${sortOption === "site" ? "bg-blue-50 text-blue-700" : "hover:bg-gray-100"}`}
                        onClick={() => handleSortClick("site")}
                      >
                        <span>Sort by Site</span>
                        {sortOption === "site" && (
                          <span>{sortDirection === "asc" ? <FaArrowUp /> : <FaArrowDown />}</span>
                        )}
                      </div>
                      <div
                        className={`px-3 py-2 cursor-pointer flex justify-between items-center text-sm ${sortOption === "date" ? "bg-blue-50 text-blue-700" : "hover:bg-gray-100"}`}
                        onClick={() => handleSortClick("date")}
                      >
                        <span>Sort by Date</span>
                        {sortOption === "date" && (
                          <span>{sortDirection === "asc" ? <FaArrowUp /> : <FaArrowDown />}</span>
                        )}
                      </div>
                      <div
                        className={`px-3 py-2 cursor-pointer flex justify-between items-center text-sm ${sortOption === "trainType" ? "bg-blue-50 text-blue-700" : "hover:bg-gray-100"}`}
                        onClick={() => handleSortClick("trainType")}
                      >
                        <span>Sort by Train Type</span>
                        {sortOption === "trainType" && (
                          <span>{sortDirection === "asc" ? <FaArrowUp /> : <FaArrowDown />}</span>
                        )}
                      </div>
                      <div
                        className={`px-3 py-2 cursor-pointer flex justify-between items-center text-sm ${sortOption === "direction" ? "bg-blue-50 text-blue-700" : "hover:bg-gray-100"}`}
                        onClick={() => handleSortClick("direction")}
                      >
                        <span>Sort by Direction</span>
                        {sortOption === "direction" && (
                          <span>{sortDirection === "asc" ? <FaArrowUp /> : <FaArrowDown />}</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto bg-white rounded-lg shadow-sm border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <tr>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 capitalize">Sr. No.</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 capitalize">Status</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 capitalize">Site Name</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 capitalize">
                  <div className="flex items-center justify-center space-x-1">
                    <FaCalendarAlt className="w-2 h-2 text-blue-500" />
                    <span>Date & Time</span>
                    <FaChevronDown className="w-2 h-2 text-gray-400" />
                  </div>
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 capitalize">Direction</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 capitalize">Train Type</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 capitalize">
                  <div className="flex items-center justify-center space-x-1">
                    <FaThermometerHalf className="w-2 h-2 text-orange-500" />
                    <span>Max Axle Temp</span>
                  </div>
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 capitalize">
                  <div className="flex items-center justify-center space-x-1">
                    <FaThermometerHalf className="w-2 h-2 text-blue-500" />
                    <span>Avg Axle Temp</span>
                  </div>
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 capitalize">
                  <div className="flex items-center justify-center space-x-1">
                    <FaThermometerHalf className="w-2 h-2 text-gray-500" />
                    <span>Ambient Temp</span>
                  </div>
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 capitalize">
                  <div className="flex items-center justify-center space-x-1">
                    <FaThermometerHalf className="w-2 h-2 text-red-500" />
                    <span>Max Wheel Temp</span>
                  </div>
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 capitalize">
                  <div className="flex items-center justify-center space-x-1">
                    <FaThermometerHalf className="w-2 h-2 text-indigo-500" />
                    <span>Avg Wheel Temp</span>
                  </div>
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 capitalize">Axles</th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 capitalize">
                  <div className="flex items-center justify-center space-x-1">
                    <FaTachometerAlt className="w-2 h-2 text-green-500" />
                    <span>Speed</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedData.map((train, index) => (
                <tr key={train.id} className="hover:bg-blue-50 transition-colors">
                  <td className="px-2 py-2 text-xs text-gray-700 text-center">{startIndex + index + 1}</td>
                  <td className="px-2 py-2 flex justify-center items-center h-full">{getStatusIcon(train.status)}</td>
                  <td className="px-2 py-2 text-xs text-gray-900 font-medium text-center">{train.siteName}</td>
                  <td className="px-2 py-2 text-xs text-gray-700 font-bold text-center">{train.dateTime}</td>
                  <td className="px-2 py-2 text-xs text-gray-700 text-center">
                    <span
                      className={`px-1 py-0.5 rounded-full text-xs font-medium ${train.direction === "UP"
                        ? "bg-green-100 text-green-700 border border-green-200"
                        : "bg-blue-100 text-blue-700 border border-blue-200"
                        }`}
                    >
                      {train.direction}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-700 text-center">{train.trainType}</td>
                  <td className="px-2 py-2 text-xs text-orange-600 font-medium text-center">
                    {train.maxAxleTemp !== "N/A" ? `${train.maxAxleTemp} °C` : "N/A"}
                  </td>
                  <td className="px-2 py-2 text-xs text-blue-600 font-medium text-center">
                    {train.avgAxleTemp !== "N/A" ? `${train.avgAxleTemp} °C` : "N/A"}
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-600 font-medium text-center">
                    {train.ambientTemp !== "N/A" ? `${train.ambientTemp} °C` : "N/A"}
                  </td>
                  <td className="px-2 py-2 text-xs text-red-600 font-medium text-center">
                    {train.maxWheelTemp !== "N/A" ? `${train.maxWheelTemp} °C` : "N/A"}
                  </td>
                  <td className="px-2 py-2 text-xs text-indigo-600 font-medium text-center">
                    {train.avgWheelTemp !== "N/A" ? `${train.avgWheelTemp} °C` : "N/A"}
                  </td>
                  <td className="px-2 py-2 text-xs text-gray-700 text-center">{train.axles}</td>
                  <td className="px-2 py-2 text-xs text-green-600 font-medium text-center">
                    {train.speed !== "N/A" ? `${train.speed} km/h` : "N/A"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="bg-gray-50 px-3 py-2 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 space-y-1 sm:space-y-0">
            <div className="text-xs text-gray-700 text-center">
              Showing {totalEntries > 0 ? startIndex + 1 : 0} to {endIndex} of {totalEntries} entries
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className="flex items-center space-x-1 px-2 py-0.5 text-xs text-gray-600 hover:text-blue-600 disabled:opacity-50 transition-colors"
              >
                <FaChevronLeft className="w-2 h-2" />
                <span>Previous</span>
              </button>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="flex items-center space-x-1 px-2 py-0.5 text-xs text-gray-600 hover:text-blue-600 disabled:opacity-50 transition-colors"
              >
                <span>Next</span>
                <FaChevronRight className="w-2 h-2" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SiteOverview